// ===========================================
// Feature Request Modal Component
// ===========================================
// Modal for adding/editing roadmap feature requests

import React, { useState, useEffect } from 'react';
import { useProjects } from '../context/ProjectContext';
import { X, Loader, Plus, Lightbulb, Save, Trash2 } from 'lucide-react';
import './Modal.scss';

// Priority levels for feature requests
const priorityOptions = [
  { value: 'low', label: 'Low', color: '#94a3b8' },
  { value: 'medium', label: 'Medium', color: '#f59e0b' },
  { value: 'high', label: 'High', color: '#ef4444' }
];

const MilestoneModal = ({ projectId, milestone = null, onClose, onSaved }) => {
  const { addMilestone, updateMilestone, deleteMilestone } = useProjects();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  // Form state - 'milestone' field stores the feature request title
  const [formData, setFormData] = useState({
    milestone: '',
    description: '',
    priority: 'medium'
  });

  // Initialize form with milestone data if editing
  useEffect(() => {
    if (milestone) {
      setFormData({
        milestone: milestone.milestone || '',
        description: milestone.description || '',
        priority: milestone.priority || 'medium'
      });
    }
  }, [milestone]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.milestone.trim()) {
      setError('Title is required');
      return;
    }
    
    setLoading(true);
    
    // Build milestone data - status defaults to 'pending' for new items
    // Status is managed via kanban drag & drop, not this form
    const milestoneData = {
      milestone: formData.milestone,
      description: formData.description || undefined,
      priority: formData.priority,
      // Only set status for new milestones (defaults to backlog)
      ...(milestone?._id ? {} : { status: 'pending' })
    };
    
    let result;
    if (milestone?._id) {
      result = await updateMilestone(projectId, milestone._id, milestoneData);
    } else {
      result = await addMilestone(projectId, milestoneData);
    }
    
    if (result.success) {
      onSaved(result.data);
      onClose();
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  // ===========================================
  // Handle delete milestone
  // ===========================================
  const handleDelete = async () => {
    // First click shows confirmation
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    
    // Second click performs delete
    setLoading(true);
    setError('');
    
    const result = await deleteMilestone(projectId, milestone._id);
    
    if (result.success) {
      onClose();
    } else {
      setError(result.error);
      setConfirmDelete(false);
    }
    
    setLoading(false);
  };

  const isEditing = !!milestone;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal__header">
          <h2>
            <Lightbulb size={20} />
            {isEditing ? 'Edit Request' : 'Add Request'}
          </h2>
          <button className="modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form className="modal__form" onSubmit={handleSubmit}>
          {error && (
            <div className="modal__error">{error}</div>
          )}

          {/* Title */}
          <div className="modal__field">
            <label htmlFor="milestone">Title *</label>
            <input
              id="milestone"
              name="milestone"
              type="text"
              placeholder="Dark mode support, API integration, User dashboard..."
              value={formData.milestone}
              onChange={handleChange}
              disabled={loading}
              autoFocus
            />
          </div>

          {/* Description */}
          <div className="modal__field">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              placeholder="What does this feature need to accomplish?"
              value={formData.description}
              onChange={handleChange}
              disabled={loading}
              rows={3}
            />
          </div>

          {/* Priority */}
          <div className="modal__field">
            <label htmlFor="priority">Priority</label>
            <select
              id="priority"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              disabled={loading}
            >
              {priorityOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="modal__actions">
            {/* Delete button - only show when editing existing milestone */}
            {isEditing && (
              <button 
                type="button" 
                className={`btn-danger ${confirmDelete ? 'btn-danger--confirm' : ''}`}
                onClick={handleDelete}
                disabled={loading}
                style={{ marginRight: 'auto' }}
              >
                <Trash2 size={16} />
                {confirmDelete ? 'Click to Confirm' : 'Delete'}
              </button>
            )}
            <button 
              type="button" 
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  {isEditing ? 'Saving...' : 'Adding...'}
                </>
              ) : (
                <>
                  {isEditing ? <Save size={16} /> : <Plus size={16} />}
                  {isEditing ? 'Save Request' : 'Add Request'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MilestoneModal;

